// frontend/src/pages/Institution/AddInstitution.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Home, Save, X, AlertCircle, Upload } from 'lucide-react';
import './Institution.css';

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AddInstitution = () => {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  const [filePreviews, setFilePreviews] = useState({
    institution_logo: null,
    institution_seal: null,
    institution_signature: null
  });
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const navigate = useNavigate();

  const [data, setData] = useState({
    institution_name: "",
    acronym: "",
    institution_type: "",
    current_manager: "",
    contact: "",
    institution_location: "",
    institution_logo: null,
    institution_seal: null,
    institution_signature: null,
  });

  // Fetch institution types
  useEffect(() => {
    const fetchInstitutionTypes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${url}/api/institution/types`, {
          headers: getAuthHeaders(),
        });
        if (response.data.success) {
          setInstitutionTypes(response.data.data);
        }
      } catch (error) {
        toast.error("Failed to load institution types");
      } finally {
        setLoading(false);
      }
    };
    fetchInstitutionTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) => setFocused({ ...focused, [field]: data[field] && data[field].length > 0 });

  const onChangeHandler = (event) => {
    const { name, value, files } = event.target;

    if (files && files[0]) {
      const file = files[0];
      setData(prev => ({ ...prev, [name]: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreviews(prev => ({ ...prev, [name]: reader.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setData(prev => ({ ...prev, [name]: value }));
      // Clear error for this field
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!data.institution_name.trim()) {
      newErrors.institution_name = "Institution name is required";
    }

    if (!data.institution_type) {
      newErrors.institution_type = "Institution type is required";
    }

    if (data.contact && !/^[\+]?[1-9][\d]{0,15}$/.test(data.contact)) {
      newErrors.contact = "Invalid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // File input handlers
  const triggerFileInput = (fieldId) => {
    document.getElementById(fieldId).click();
  };

  const onSubmitHandler = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();

      // Add text fields
      formData.append("institution_name", data.institution_name);
      formData.append("acronym", data.acronym);
      formData.append("institution_type", data.institution_type);
      formData.append("current_manager", data.current_manager);
      formData.append("contact", data.contact);
      formData.append("institution_location", data.institution_location);

      // Add files if they exist
      if (data.institution_logo instanceof File) {
        formData.append("institution_logo", data.institution_logo);
      }
      if (data.institution_seal instanceof File) {
        formData.append("institution_seal", data.institution_seal);
      }
      if (data.institution_signature instanceof File) {
        formData.append("institution_signature", data.institution_signature);
      }

      const response = await axios.post(
        `${url}/api/institution`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getAuthHeaders(),
          },
        }
      );

      if (response.data.success) {
        toast.success("Institution created successfully!");
        setData({
          institution_name: "",
          acronym: "",
          institution_type: "",
          current_manager: "",
          contact: "",
          institution_location: "",
          institution_logo: null,
          institution_seal: null,
          institution_signature: null,
        });
        setFilePreviews({
          institution_logo: null,
          institution_seal: null,
          institution_signature: null
        });
        navigate("/ViewInstitution");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to create institution";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewInstitution");
  };

  const removeFile = (field) => {
    setData(prev => ({ ...prev, [field]: null }));
    setFilePreviews(prev => ({ ...prev, [field]: null }));
    document.getElementById(field).value = "";
  };

  return (
    <div className="form-wrapper">
      <div className="currency-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <Building2 size={24} />
          <h2 className="form-title">Add New Institution</h2>
          <p className="form-subtitle">
            Fill in the institution details below. All fields marked with * are required.
          </p>
        </div>

        <form className="currency-form" onSubmit={onSubmitHandler} encType="multipart/form-data">
          <div className="form-grid">
            {/* Institution Name */}
            <div className={`form-field required ${focused.institution_name || data.institution_name ? "filled" : ""} ${errors.institution_name ? 'error' : ''}`}>
              <input
                type="text"
                name="institution_name"
                value={data.institution_name}
                onChange={onChangeHandler}
                onFocus={() => handleFocus("institution_name")}
                onBlur={() => handleBlur("institution_name")}
                placeholder="Enter institution name"
                required
              />
              <label>Institution Name *</label>
              {errors.institution_name && <small className="field-error">{errors.institution_name}</small>}
            </div>

            {/* Acronym */}
            <div className={`form-field ${focused.acronym || data.acronym ? "filled" : ""} ${errors.acronym ? 'error' : ''}`}>
              <input
                type="text"
                name="acronym"
                value={data.acronym}
                onChange={onChangeHandler}
                onFocus={() => handleFocus("acronym")}
                onBlur={() => handleBlur("acronym")}
                placeholder="Enter acronym (e.g., MIT)"
              />
              <label>Acronym</label>
              {errors.acronym && <small className="field-error">{errors.acronym}</small>}
            </div>

            {/* Institution Type */}
            <div className={`form-field required ${focused.institution_type || data.institution_type ? "filled" : ""} ${errors.institution_type ? 'error' : ''}`}>
              <select
                name="institution_type"
                value={data.institution_type}
                onChange={onChangeHandler}
                onFocus={() => handleFocus("institution_type")}
                onBlur={() => handleBlur("institution_type")}
                required
                disabled={loading}
              >
                <option value="">Select Institution Type</option>
                {institutionTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
                <option value="ICT">ICT</option>
              </select>
              <label>Institution Type *</label>
              {errors.institution_type && <small className="field-error">{errors.institution_type}</small>}
              {loading && <small className="field-hint">Loading types...</small>}
            </div>

            {/* Current Manager */}
            <div className={`form-field ${focused.current_manager || data.current_manager ? "filled" : ""}`}>
              <input
                type="text"
                name="current_manager"
                value={data.current_manager}
                onChange={onChangeHandler}
                onFocus={() => handleFocus("current_manager")}
                onBlur={() => handleBlur("current_manager")}
                placeholder="Enter current manager/director"
              />
              <label>Current Manager</label>
            </div>

            {/* Contact */}
            <div className={`form-field ${focused.contact || data.contact ? "filled" : ""} ${errors.contact ? 'error' : ''}`}>
              <input
                type="text"
                name="contact"
                value={data.contact}
                onChange={onChangeHandler}
                onFocus={() => handleFocus("contact")}
                onBlur={() => handleBlur("contact")}
                placeholder="Enter contact number"
              />
              <label>Contact Number</label>
              {errors.contact && <small className="field-error">{errors.contact}</small>}
            </div>

            {/* Institution Location */}
            <div className={`form-field ${focused.institution_location || data.institution_location ? "filled" : ""}`}>
              <input
                type="text"
                name="institution_location"
                value={data.institution_location}
                onChange={onChangeHandler}
                onFocus={() => handleFocus("institution_location")}
                onBlur={() => handleBlur("institution_location")}
                placeholder="Enter institution location/address"
              />
              <label>Institution Location</label>
            </div>

            {/* Institution Logo */}
            <div className="form-field file-upload">
              <label>Institution Logo</label>
              <input
                type="file"
                id="institution_logo"
                name="institution_logo"
                accept="image/*"
                onChange={onChangeHandler}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              <div
                className="file-upload-area"
                onClick={() => triggerFileInput("institution_logo")}
                style={{ cursor: 'pointer' }}
              >
                <div className="upload-content">
                  {filePreviews.institution_logo ? (
                    <div className="file-preview">
                      <img src={filePreviews.institution_logo} alt="Logo preview" />
                      <button
                        type="button"
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile("institution_logo");
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={24} />
                      <span>Click to upload logo</span>
                      <small>Recommended: 200x200px, PNG/JPG</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Institution Seal */}
            <div className="form-field file-upload">
              <label>Institution Seal</label>
              <input
                type="file"
                id="institution_seal"
                name="institution_seal"
                accept="image/*"
                onChange={onChangeHandler}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              <div
                className="file-upload-area"
                onClick={() => triggerFileInput("institution_seal")}
                style={{ cursor: 'pointer' }}
              >
                <div className="upload-content">
                  {filePreviews.institution_seal ? (
                    <div className="file-preview">
                      <img src={filePreviews.institution_seal} alt="Seal preview" />
                      <button
                        type="button"
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile("institution_seal");
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={24} />
                      <span>Click to upload seal</span>
                      <small>Official seal/stamp</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Institution Signature */}
            <div className="form-field file-upload">
              <label>Institution Signature</label>
              <input
                type="file"
                id="institution_signature"
                name="institution_signature"
                accept="image/*"
                onChange={onChangeHandler}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              <div
                className="file-upload-area"
                onClick={() => triggerFileInput("institution_signature")}
                style={{ cursor: 'pointer' }}
              >
                <div className="upload-content">
                  {filePreviews.institution_signature ? (
                    <div className="file-preview">
                      <img src={filePreviews.institution_signature} alt="Signature preview" />
                      <button
                        type="button"
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile("institution_signature");
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={24} />
                      <span>Click to upload signature</span>
                      <small>Digital signature image</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-info">
            <AlertCircle size={14} />
            <small>Only image files (JPG, PNG, GIF, SVG) up to 5MB are allowed.</small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              <Home size={16} className="mr-2" />
              Back to List
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="spinner"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Create Institution
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInstitution;